import { useState, useEffect } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, Clock, AlertCircle, CheckCircle, XCircle, Send, Paperclip, Download, Trash2 } from "lucide-react";
import { ticketService } from "@/services/ticketService";
import { clientTimelineService, type TicketTimeline } from "@/services/clientTimelineService";
import { attachmentService } from "@/services/attachmentService";

interface TicketDetails {
  id: string;
  codigo: string;
  titulo: string;
  descricao: string;
  prioridade: 'BAIXA' | 'MEDIA' | 'ALTA';
  status: 'ABERTO' | 'EM_ANDAMENTO' | 'AGUARDANDO_CLIENTE' | 'AGUARDANDO_APROVACAO' | 'RESOLVIDO' | 'FECHADO';
  createdAt: string;
  updatedAt: string;
  aprovado: boolean;
  aprovadoEm?: string | null;
  emergencial?: boolean;
  horasEstimadas?: number | null;
  valorEstimado?: number | null;
  client: {
    id: string;
    razaoSocial: string;
    nomeFantasia?: string | null;
  };
  apontamentos?: Array<{
    id: string;
    data: string;
    horaInicio: string;
    horaFim: string;
    minutosTotais: number;
    tipoHora: string;
    valorHora?: string | number;
    descricao: string;
    user: {
      id: string;
      name: string;
    };
  }>;
}

const statusConfig = {
  ABERTO: {
    label: "Aberto",
    icon: AlertCircle,
    color: "text-red-500",
    bgColor: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300"
  },
  EM_ANDAMENTO: {
    label: "Em Andamento",
    icon: Clock,
    color: "text-blue-500",
    bgColor: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-300"
  },
  AGUARDANDO_CLIENTE: {
    label: "Aguardando Cliente",
    icon: Clock,
    color: "text-yellow-500",
    bgColor: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300"
  },
  AGUARDANDO_APROVACAO: {
    label: "Aguardando Aprovação",
    icon: Clock,
    color: "text-gray-500",
    bgColor: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
  },
  RESOLVIDO: {
    label: "Resolvido",
    icon: CheckCircle,
    color: "text-green-500",
    bgColor: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300"
  },
  FECHADO: {
    label: "Fechado",
    icon: XCircle,
    color: "text-gray-500",
    bgColor: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-300"
  }
};

const prioridadeConfig = {
  BAIXA: { label: "Baixa", color: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-300" },
  MEDIA: { label: "Média", color: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-300" },
  ALTA: { label: "Alta", color: "bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-300" }
};

export function PortalTicketDetails() {
  const { id } = useParams<{ id: string }>();
  const [ticket, setTicket] = useState<TicketDetails | null>(null);
  const [timeline, setTimeline] = useState<TicketTimeline | null>(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("timeline");
  
  // Estados para resposta do cliente
  const [clientResponse, setClientResponse] = useState("");
  const [isSubmittingResponse, setIsSubmittingResponse] = useState(false);
  const [responseSuccess, setResponseSuccess] = useState(false);
  
  // Estados para upload de anexos
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [isUploading, setIsUploading] = useState(false);

  // Estado para aprovação de demanda (catraca)
  const [approving, setApproving] = useState(false);
  const [approveError, setApproveError] = useState<string | null>(null);

  // Estado para rejeição/pedido de ajuste
  const [showReject, setShowReject] = useState(false);
  const [rejectReason, setRejectReason] = useState('');
  const [rejecting, setRejecting] = useState(false);
  const [rejectError, setRejectError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      loadTicketData(id);
    }
  }, [id]);

  const loadTicketData = async (ticketId: string) => {
    try {
      setLoading(true);
      
      // Carregar dados básicos do ticket
      const ticketResponse = await ticketService.getById(ticketId);
      setTicket(ticketResponse);
      
      // Carregar timeline (apontamentos visíveis ao cliente)
      const timelineResponse = await clientTimelineService.getTimeline(ticketId);
      setTimeline(timelineResponse);
    } catch (error) {
      console.error('Erro ao carregar dados do ticket:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      setSelectedFiles(prev => [...prev, ...files]);
    }
  };

  const removeSelectedFile = (index: number) => {
    setSelectedFiles(prev => prev.filter((_, i) => i !== index));
  };

  const handleSubmitResponse = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!clientResponse.trim() || clientResponse.trim().length < 10) {
      return;
    }

    setIsSubmittingResponse(true);

    try {
      // Enviar resposta do cliente e obter o ID do apontamento criado
      const response = await clientTimelineService.addResponse(id!, clientResponse.trim());
      
      // Upload de anexos se houver, associando ao apontamento criado
      if (selectedFiles.length > 0 && response.apontamento?.id) {
        setIsUploading(true);
        for (const file of selectedFiles) {
          await attachmentService.upload(id!, file, response.apontamento.id);
        }
      }

      // Limpar formulário
      setClientResponse("");
      setSelectedFiles([]);
      setResponseSuccess(true);
      
      // Recarregar timeline
      await loadTicketData(id!);
      
      // Ocultar mensagem de sucesso após 3 segundos
      setTimeout(() => setResponseSuccess(false), 3000);
      
    } catch (error: any) {
      console.error('Erro ao enviar resposta:', error);
    } finally {
      setIsSubmittingResponse(false);
      setIsUploading(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId: string) => {
    try {
      await attachmentService.download(id!, attachmentId);
    } catch (error) {
      console.error('Erro ao fazer download:', error);
    }
  };

  const handleApprove = async () => {
    setApproving(true);
    setApproveError(null);
    try {
      await ticketService.aprovar(id!);
      await loadTicketData(id!);
    } catch (error: any) {
      setApproveError(error?.response?.data?.error || 'Erro ao aprovar o chamado');
    } finally {
      setApproving(false);
    }
  };

  const handleReject = async () => {
    if (rejectReason.trim().length < 10) {
      setRejectError('Informe o motivo do ajuste (mínimo 10 caracteres)');
      return;
    }
    setRejecting(true);
    setRejectError(null);
    try {
      await ticketService.rejeitar(id!, rejectReason.trim());
      await loadTicketData(id!);
      setShowReject(false);
      setRejectReason('');
    } catch (error: any) {
      setRejectError(error?.response?.data?.error || 'Erro ao rejeitar o chamado');
    } finally {
      setRejecting(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDateOnly = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  const formatTime = (dateString: string) => {
    return new Date(dateString).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/portal" className="btn btn-ghost btn-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Link>
        </div>
        
        <div className="card animate-pulse">
          <div className="card-header">
            <div className="h-6 bg-muted rounded w-1/4 mb-2"></div>
            <div className="h-8 bg-muted rounded w-3/4"></div>
          </div>
          <div className="card-content">
            <div className="space-y-2">
              <div className="h-4 bg-muted rounded w-full"></div>
              <div className="h-4 bg-muted rounded w-2/3"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (!ticket) {
    return (
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Link to="/portal" className="btn btn-ghost btn-sm">
            <ArrowLeft className="h-4 w-4 mr-2" />
            Voltar
          </Link>
        </div>
        
        <div className="card">
          <div className="card-content flex flex-col items-center justify-center py-12">
            <AlertCircle className="h-12 w-12 text-muted-foreground mb-4" />
            <h3 className="text-lg font-semibold mb-2">Ticket não encontrado</h3>
            <p className="text-muted-foreground">O ticket solicitado não existe ou você não tem acesso a ele.</p>
          </div>
        </div>
      </div>
    );
  }

  const StatusIcon = statusConfig[ticket.status].icon;

  return (
    <div className="space-y-6">
      {/* Header com botão voltar */}
      <div className="flex items-center gap-4">
        <Link to="/portal" className="btn btn-ghost btn-sm">
          <ArrowLeft className="h-4 w-4 mr-2" />
          Voltar
        </Link>
      </div>

      {/* Banner de aprovação de demanda (catraca) */}
      {ticket.status === 'AGUARDANDO_APROVACAO' && !ticket.aprovado && (
        <div className="card border border-border">
          <div className="card-content">
            <div className="flex flex-col md:flex-row md:items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-muted rounded-full flex items-center justify-center">
                <CheckCircle className="w-6 h-6 text-muted-foreground" />
              </div>
              <div className="flex-1">
                <h3 className="text-lg font-semibold text-foreground">
                  Aguardando sua aprovação
                </h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {ticket.aprovadoEm
                    ? 'Horas adicionais foram apontadas e precisam da sua aprovação antes de continuar.'
                    : 'Este chamado possui uma estimativa de trabalho e precisa da sua aprovação antes de iniciar a execução.'}
                </p>
                {!ticket.aprovadoEm && (ticket.horasEstimadas != null || ticket.valorEstimado != null) && (
                  <div className="flex flex-wrap gap-3 mt-3">
                    {ticket.horasEstimadas != null && (
                      <span className="px-3 py-1 bg-muted rounded-full text-sm font-medium">
                        Horas estimadas: {ticket.horasEstimadas}h
                      </span>
                    )}
                    {ticket.valorEstimado != null && (
                      <span className="px-3 py-1 bg-muted rounded-full text-sm font-medium">
                        Valor estimado: R$ {ticket.valorEstimado.toFixed(2)}
                      </span>
                    )}
                  </div>
                )}
                {(ticket.apontamentos || []).filter((a) => a.tipoHora !== 'PROJETO' && !(a as any).faturado).length > 0 && (
                  <div className="mt-3 space-y-2">
                    <p className="text-sm font-medium text-muted-foreground">
                      Horas para aprovação:
                    </p>
                    {(ticket.apontamentos || [])
                      .filter((a) => a.tipoHora !== 'PROJETO' && !(a as any).faturado)
                      .map((a) => (
                        <div key={a.id} className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-lg p-3 border border-border">
                          <span className="text-sm font-medium text-foreground">
                            {a.tipoHora} — {Math.floor(a.minutosTotais / 60)}h{a.minutosTotais % 60}m
                          </span>
                          <span className="text-xs text-muted-foreground truncate max-w-xs">{a.descricao}</span>
                          <span className="text-sm font-semibold text-foreground ml-auto">
                            {new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(
                              (a.minutosTotais / 60) * Number(a.valorHora || 0)
                            )}
                          </span>
                        </div>
                      ))}
                  </div>
                )}
                {approveError && (
                  <p className="text-sm text-red-600 dark:text-red-400 mt-2">{approveError}</p>
                )}
              </div>
              <div className="flex flex-col gap-2">
                <button
                  onClick={handleApprove}
                  disabled={approving}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-foreground hover:bg-foreground/90 text-background rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {approving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                      Aprovando...
                    </>
                  ) : (
                    <>
                      <CheckCircle className="w-5 h-5" />
                      Aprovar Demanda
                    </>
                  )}
                </button>
                <button
                  onClick={() => { setShowReject(!showReject); setRejectError(null); }}
                  disabled={approving}
                  className="inline-flex items-center justify-center gap-2 px-6 py-3 border border-border text-foreground rounded-lg font-semibold transition-colors hover:bg-muted disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <XCircle className="w-5 h-5" />
                  Não aprovar / pedir ajuste
                </button>
              </div>
            </div>

            {showReject && (
              <div className="mt-4 space-y-3 border-t border-border pt-4">
                <p className="text-sm text-muted-foreground">
                  Informe o motivo do ajuste para que nossa equipe revise a estimativa.
                </p>
                <textarea
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Ex: O valor está acima do esperado; gostaria de reduzir o escopo..."
                  rows={3}
                  className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-primary transition-colors resize-none"
                />
                {rejectError && (
                  <p className="text-sm text-red-600 dark:text-red-400">{rejectError}</p>
                )}
                <div className="flex items-center gap-3">
                  <button
                    onClick={handleReject}
                    disabled={rejecting}
                    className="inline-flex items-center gap-2 px-6 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {rejecting ? (
                      <>
                        <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Enviando...
                      </>
                    ) : (
                      <>
                        <Send className="w-4 h-4" />
                        Enviar Pedido de Ajuste
                      </>
                    )}
                  </button>
                  <button
                    onClick={() => { setShowReject(false); setRejectReason(''); setRejectError(null); }}
                    className="px-4 py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                  >
                    Cancelar
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Banner de atendimento emergencial (auto-aprovado) */}
      {ticket.emergencial && (
        <div className="card border-2 border-red-300 dark:border-red-700">
          <div className="card-content">
            <div className="flex items-center gap-4">
              <div className="flex-shrink-0 w-12 h-12 bg-red-100 dark:bg-red-900/40 rounded-full flex items-center justify-center">
                <AlertCircle className="w-6 h-6 text-red-600 dark:text-red-400" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-red-900 dark:text-red-200">
                  Atendimento Emergencial
                </h3>
                <p className="text-sm text-red-800 dark:text-red-300 mt-1">
                  Este chamado foi marcado como emergencial e já está <strong>aprovado</strong> — nossa equipe iniciou o atendimento imediatamente, sem necessidade de aprovação prévia.
                </p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Informações principais do ticket */}
      <div className="card">
        <div className="card-header">
          <div className="flex items-start justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <h1 className="text-2xl font-bold">{ticket.codigo}</h1>
                <span className={`badge inline-flex items-center gap-1 ${statusConfig[ticket.status].bgColor}`}>
                  <StatusIcon className="h-3 w-3" />
                  {statusConfig[ticket.status].label}
                </span>
              </div>
              <p className="text-lg font-medium text-foreground">
                {ticket.titulo}
              </p>
            </div>
            
            <span className={`badge ${prioridadeConfig[ticket.prioridade].color}`}>
              Prioridade {prioridadeConfig[ticket.prioridade].label}
            </span>
          </div>
        </div>
        
        <div className="card-content">
          <div className="space-y-4">
            <div>
              <h4 className="font-semibold mb-2">Descrição</h4>
              <p className="text-muted-foreground whitespace-pre-wrap">
                {ticket.descricao}
              </p>
            </div>
            
            <hr className="border-border" />
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              <div>
                <span className="font-medium">Criado em:</span>
                <p className="text-muted-foreground">{formatDate(ticket.createdAt)}</p>
              </div>
              <div>
                <span className="font-medium">Última atualização:</span>
                <p className="text-muted-foreground">{formatDate(ticket.updatedAt)}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Tabs para diferentes seções */}
      <div className="space-y-4">
        <div className="flex border-b border-border">
          <button
            onClick={() => setActiveTab("timeline")}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
              activeTab === "timeline"
                ? "border-brand text-brand"
                : "border-transparent text-muted-foreground hover:text-foreground"
            }`}
          >
            Timeline de Interações
          </button>
        </div>
        
        {activeTab === "timeline" && (
          <div className="space-y-6">
            {/* Timeline de Interações */}
            <div className="card">
              <div className="card-header">
                <h3 className="text-lg font-semibold">Histórico de Interações</h3>
                <p className="text-muted-foreground">
                  Acompanhe o progresso e comunicação sobre seu ticket
                </p>
              </div>
              <div className="card-content">
                {!timeline || timeline.timeline.length === 0 ? (
                  <div className="text-center py-8">
                    <Clock className="h-8 w-8 text-muted-foreground mx-auto mb-2" />
                    <p className="text-muted-foreground">Nenhuma interação registrada ainda</p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Nossa equipe entrará em contato em breve
                    </p>
                  </div>
                ) : (
                  <div className="space-y-6">
                    {timeline.timeline.map((item, index) => {
                      const isClient = /^\[(RESPOSTA|APROVAÇÃO|REJEIÇÃO) DO CLIENTE\]/.test(item.descricao);
                      const cleanDescription = item.descricao.replace(/^\[[^\]]+\]\s*/, '');
                      return (
                      <div key={item.id} className="flex gap-4">
                        <div className="flex flex-col items-center">
                          <div className="w-3 h-3 bg-brand rounded-full"></div>
                          {index < timeline.timeline.length - 1 && (
                            <div className="w-px h-20 bg-border mt-2"></div>
                          )}
                        </div>
                        
                        <div className="flex-1 pb-4">
                          <div className="flex items-center gap-2 mb-2">
                            <span className="font-normal text-foreground">
                              {isClient ? 'Você' : item.user.name}
                            </span>
                            <span className="text-sm text-muted-foreground">
                              {formatDateOnly(item.data)} • {formatTime(item.horaInicio)} - {formatTime(item.horaFim)}
                            </span>
                          </div>
                          
                          <div className="bg-muted/30 rounded-lg p-4 mb-3">
                            <p className="text-foreground whitespace-pre-wrap">
                              {cleanDescription}
                            </p>
                          </div>
                          
                          {/* Anexos do apontamento */}
                          {item.anexos && item.anexos.length > 0 && (
                            <div className="space-y-2">
                              <p className="text-sm font-medium text-muted-foreground">Anexos:</p>
                              <div className="flex flex-wrap gap-2">
                                {item.anexos.map((anexo) => (
                                  <button
                                    key={anexo.id}
                                    onClick={() => handleDownloadAttachment(anexo.id)}
                                    className="inline-flex items-center gap-2 px-3 py-2 bg-background border border-border rounded-lg hover:bg-muted/50 transition-colors text-sm"
                                  >
                                    <span>{attachmentService.getFileIcon(anexo.mimetype)}</span>
                                    <span className="truncate max-w-[200px]">{anexo.filename}</span>
                                    <span className="text-muted-foreground">
                                      ({attachmentService.formatFileSize(anexo.size)})
                                    </span>
                                    <Download className="w-4 h-4 text-muted-foreground" />
                                  </button>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>

            {/* Formulário de Resposta do Cliente */}
            {ticket.status === 'AGUARDANDO_CLIENTE' ? (
              <div className="card">
                <div className="card-header">
                  <h3 className="text-lg font-semibold">Adicionar Resposta</h3>
                  <p className="text-muted-foreground">
                    Envie informações adicionais ou responda às solicitações da equipe
                  </p>
                </div>
                <div className="card-content">
                  {responseSuccess && (
                    <div className="mb-4 p-4 bg-green-500/10 border border-green-500/20 rounded-lg">
                      <div className="flex items-center gap-2 text-green-600">
                        <CheckCircle className="w-5 h-5" />
                        <span className="font-medium">Resposta enviada com sucesso!</span>
                      </div>
                      <p className="text-sm text-green-600/80 mt-1">
                        Nossa equipe foi notificada e entrará em contato em breve.
                      </p>
                    </div>
                  )}

                  <form onSubmit={handleSubmitResponse} className="space-y-4">
                    {/* Área de texto */}
                    <div>
                      <label htmlFor="clientResponse" className="block text-sm font-medium text-foreground mb-2">
                        Sua mensagem *
                      </label>
                      <textarea
                        id="clientResponse"
                        value={clientResponse}
                        onChange={(e) => setClientResponse(e.target.value)}
                        placeholder="Descreva informações adicionais, responda às perguntas da equipe ou relate novos desenvolvimentos..."
                        rows={4}
                        className="w-full px-4 py-3 bg-background border border-border rounded-lg text-foreground placeholder:text-muted-foreground focus:border-primary transition-colors resize-none"
                        disabled={isSubmittingResponse}
                      />
                      <div className="text-xs text-muted-foreground mt-1">
                        Mínimo 10 caracteres
                      </div>
                    </div>

                    {/* Upload de Anexos */}
                    <div>
                      <label className="block text-sm font-medium text-foreground mb-2">
                        Anexos (opcional)
                      </label>
                      
                      <div className="space-y-3">
                        {/* Botão de upload */}
                        <div className="flex items-center gap-3">
                          <input
                            type="file"
                            id="fileUpload"
                            multiple
                            accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.txt,.csv,.zip"
                            onChange={handleFileSelect}
                            className="hidden"
                            disabled={isSubmittingResponse || isUploading}
                          />
                          <label
                            htmlFor="fileUpload"
                            className="inline-flex items-center gap-2 px-4 py-2 bg-muted hover:bg-muted/80 border border-border rounded-lg cursor-pointer transition-colors text-sm"
                          >
                            <Paperclip className="w-4 h-4" />
                            Anexar Arquivos
                          </label>
                          <span className="text-xs text-muted-foreground">
                            PDF, imagens, TXT, CSV, ZIP (máx. 10MB cada)
                          </span>
                        </div>

                        {/* Lista de arquivos selecionados */}
                        {selectedFiles.length > 0 && (
                          <div className="space-y-2">
                            <p className="text-sm font-medium text-foreground">Arquivos selecionados:</p>
                            <div className="space-y-2">
                              {selectedFiles.map((file, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between p-3 bg-muted/30 rounded-lg"
                                >
                                  <div className="flex items-center gap-3">
                                    <span>{attachmentService.getFileIcon(file.type)}</span>
                                    <div>
                                      <p className="text-sm font-medium text-foreground">{file.name}</p>
                                      <p className="text-xs text-muted-foreground">
                                        {attachmentService.formatFileSize(file.size)}
                                      </p>
                                    </div>
                                  </div>
                                  <button
                                    type="button"
                                    onClick={() => removeSelectedFile(index)}
                                    className="p-1 hover:bg-red-500/10 rounded text-red-500 transition-colors"
                                    disabled={isSubmittingResponse || isUploading}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Botões */}
                    <div className="flex items-center gap-3 pt-2">
                      <button
                        type="submit"
                        disabled={
                          isSubmittingResponse || 
                          isUploading || 
                          !clientResponse.trim() || 
                          clientResponse.trim().length < 10
                        }
                        className="inline-flex items-center gap-2 px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {isSubmittingResponse || isUploading ? (
                          <>
                            <div className="w-4 h-4 border-2 border-primary-foreground/30 border-t-primary-foreground rounded-full animate-spin" />
                            {isUploading ? 'Enviando anexos...' : 'Enviando resposta...'}
                          </>
                        ) : (
                          <>
                            <Send className="w-4 h-4" />
                            Enviar Resposta
                          </>
                        )}
                      </button>
                      
                      <div className="text-xs text-muted-foreground">
                        Sua resposta será enviada para nossa equipe de suporte
                      </div>
                    </div>
                  </form>
                </div>
              </div>
            ) : (
              <div className="card">
                <div className="card-content">
                  <div className="flex items-center gap-3 p-4 bg-blue-500/10 border border-blue-500/20 rounded-lg">
                    <Clock className="w-5 h-5 text-blue-600 flex-shrink-0" />
                    <div>
                      <p className="font-medium text-foreground">Ticket em atendimento</p>
                      <p className="text-sm text-muted-foreground mt-1">
                        Nossa equipe está trabalhando no seu chamado. Você poderá responder quando solicitarmos informações adicionais.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}